// Phase C · Diminuisci/Aumenta una singola sottoriga misura.
// Riusa i motori BACKEND certificati (stralcio 5B-2b / rettifica positiva 5C). La conferma rende
// EFFICACE la variazione. Preview = nessuna scrittura. Nessun calcolo economico lato client.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { salApi } from '../../lib/api';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, AlertTriangle, Loader2 } from 'lucide-react';
import { fmtQty, fmtEur, parseNum } from '../../lib/formatQty';

const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `op-${Date.now()}-${Math.random().toString(16).slice(2)}`);

export const MeasureRowVariationDialog = ({ open, onOpenChange, item, row, mode, onDone }) => {
  const isDecrease = mode === 'decrease';
  const unit = item?.unit || '';
  const current = Number(row?.current_quantity) || 0;
  const executed = Number(row?.executed_quantity) || 0;
  const maxReducible = Math.max(0, current - executed);

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const opKeyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    opKeyRef.current = uuid();
    setAmount(''); setReason(''); setPreview(null); setPreviewError(null); setSubmitting(false);
  }, [open]);

  const amt = parseNum(amount);
  const resulting = isDecrease ? Math.max(0, current - (amt || 0)) : current + (amt || 0);

  const fetchPreview = useCallback(async () => {
    if (isNaN(amt) || amt <= 0) { setPreview(null); setPreviewError(null); return; }
    const reasonStub = reason.trim().length >= 6 ? reason.trim() : 'anteprima__________';
    setLoading(true);
    try {
      if (isDecrease) {
        const res = await salApi.previewQuantityStralcio(item.id, {
          quantity_to_remove: amt, reason: reasonStub, measurement_row_id: row.source_measurement_id,
        });
        const d = res.data;
        setPreview({ dir: 'negative', unitPrice: d.contract_unit_price_net, net: -Math.abs(d.net_reduction),
          before: d.current_quantity_before, after: d.current_quantity_after, completion: d.completion_percentage_after });
      } else {
        const res = await salApi.previewQuantityIncrease(item.id, {
          measurement_row_id: row.source_measurement_id, proposed_row_quantity: current + amt, reason: reasonStub,
        });
        const d = res.data;
        setPreview({ dir: 'positive', unitPrice: d.contract_unit_price_net, net: Math.abs(d.net_delta),
          before: d.current_quantity_before, after: d.proposed_current_quantity, completion: d.completion_percentage_after });
      }
      setPreviewError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      setPreview(null);
      setPreviewError(detail.message || 'Anteprima non disponibile');
    } finally { setLoading(false); }
  }, [amt, reason, isDecrease, item?.id, row?.source_measurement_id, current]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(fetchPreview, 300);
    return () => clearTimeout(t);
  }, [open, amount, fetchPreview]);

  const canConfirm = preview && !previewError && !loading && reason.trim().length >= 6 && amt > 0 && !submitting;

  const confirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      if (isDecrease) {
        await salApi.applyQuantityStralcio(item.id, {
          quantity_to_remove: amt, reason: reason.trim(),
          measurement_row_id: row.source_measurement_id, idempotency_key: opKeyRef.current,
        });
      } else {
        await salApi.applyQuantityIncrease(item.id, {
          measurement_row_id: row.source_measurement_id, proposed_row_quantity: current + amt,
          reason: reason.trim(), idempotency_key: opKeyRef.current,
        });
      }
      toast.success(isDecrease ? 'Diminuzione applicata: variazione contrattuale registrata.'
                               : 'Aumento applicato: variazione contrattuale registrata.');
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Errore durante la variazione'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="measure-row-variation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#3C3C3C]">
            {isDecrease ? <TrendingDown className="w-4 h-4 text-red-600" /> : <TrendingUp className="w-4 h-4 text-emerald-600" />}
            {isDecrease ? 'Diminuisci misura' : 'Aumenta misura'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-[#737373]">
            {item.code} · {item.description}
            <div className="text-[#FF7819]">Dettaglio: {row?.description || row?.source_measurement_id}</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Cell label="Originale" value={`${fmtQty(row?.original_calculated_quantity)} ${unit}`} testid="row-var-original" />
            <Cell label="Corrente" value={`${fmtQty(current)} ${unit}`} testid="row-var-current" />
            <Cell label="Eseguita" value={`${fmtQty(executed)} ${unit}`} testid="row-var-executed" />
          </div>
          {isDecrease && (
            <div className="text-[11px] text-[#a3a3a3]" data-testid="row-var-max">
              Massimo diminuibile: <strong className="text-[#3C3C3C]">{fmtQty(maxReducible)} {unit}</strong>
            </div>
          )}
          <div>
            <Label className="text-xs">{isDecrease ? 'Quantità da diminuire' : 'Quantità da aumentare'}</Label>
            <Input type="text" inputMode="decimal" value={amount}
              onChange={(e) => setAmount(e.target.value)} className="h-9 input-abitalo mt-1"
              placeholder="0" data-testid="row-var-amount-input" />
            <div className="text-[11px] text-[#a3a3a3] mt-1" data-testid="row-var-resulting">
              Quantità risultante: <strong className="text-[#3C3C3C]">{fmtQty(resulting)} {unit}</strong>
            </div>
          </div>
          {loading && <div className="flex items-center gap-2 text-xs text-[#737373]"><Loader2 className="w-3 h-3 animate-spin" /> Calcolo impatto…</div>}
          {previewError && !loading && (
            <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 rounded p-2" data-testid="row-var-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {previewError}
            </div>
          )}
          {preview && !loading && !previewError && (
            <div className={`rounded-md border p-3 space-y-1 ${isDecrease ? 'border-red-200 bg-red-50/40' : 'border-emerald-200 bg-emerald-50/50'}`} data-testid="row-var-preview">
              <Row label="Prezzo unitario padre" value={fmtEur(preview.unitPrice)} />
              <Row label="Voce padre (prima → dopo)" value={`${fmtQty(preview.before)} → ${fmtQty(preview.after)} ${unit}`} />
              <div className="border-t border-[#e5e5e5] my-1" />
              <Row label="Valore variazione" value={`${preview.net >= 0 ? '+ ' : '- '}${fmtEur(Math.abs(preview.net))}`} strong strongClass={isDecrease ? 'text-red-600' : 'text-emerald-700'} testid="row-var-value" />
            </div>
          )}
          <div>
            <Label className="text-xs">Motivazione (obbligatoria)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="mt-1 input-abitalo text-sm" placeholder="Motivo della variazione (min 6 caratteri)"
              data-testid="row-var-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="row-var-cancel-btn">Annulla</Button>
          <Button className={isDecrease ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
            disabled={!canConfirm} onClick={confirm} data-testid="row-var-confirm-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Conferma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Cell = ({ label, value, testid }) => (
  <div className="bg-white rounded border border-[#eee] p-1.5">
    <div className="text-[#a3a3a3]">{label}</div>
    <div className="font-semibold text-[#3C3C3C]" data-testid={testid}>{value}</div>
  </div>
);
const Row = ({ label, value, strong, strongClass, testid }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-[#737373]">{label}</span>
    <span className={strong ? `font-bold ${strongClass || 'text-[#3C3C3C]'}` : 'text-[#3C3C3C]'} data-testid={testid}>{value}</span>
  </div>
);

export default MeasureRowVariationDialog;
